import { useCallback, useEffect, useRef } from 'react';
import { getLogger } from '@/idv2/utils/logger';

// A recording can't be trimmed in the browser, so two recorders take turns: each restarts every
// PERIOD_MS, OFFSET_MS apart, and one has always been running for between OFFSET_MS and PERIOD_MS.
const PERIOD_MS = 6000;
const OFFSET_MS = 3000;
const VIDEO_BITS_PER_SECOND = 3_000_000;
const STOP_TIMEOUT_MS = 2000;
// Safari only records MP4, and more vendors accept MP4 than WebM.
const MIME_TYPES = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
type Slot = {
  recorder: MediaRecorder;
  chunks: Blob[];
  startedAt: number;
};
type Timer = ReturnType<typeof setTimeout>;
type RecorderState = {
  slots: (Slot | undefined)[];
  timers: Timer[];
  clip?: Promise<Blob | undefined>;
};
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'clip-recorder'
});
const getMimeType = () => typeof MediaRecorder === 'undefined' ? undefined : MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type));
const canRecordVideo = () => getMimeType() !== undefined;
const startSlot = (stream: MediaStream, mimeType: string): Slot | undefined => {
  try {
    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: VIDEO_BITS_PER_SECOND
    });
    const slot: Slot = {
      recorder,
      chunks: [],
      startedAt: Date.now()
    };
    recorder.ondataavailable = event => {
      if (event.data.size > 0) slot.chunks.push(event.data);
    };
    recorder.onerror = event => logWarn('Selfie video recorder failed', event);
    recorder.start();
    return slot;
  } catch (error) {
    logWarn('Selfie video recorder failed to start', error);
    return undefined;
  }
};
const stopSlot = (slot: Slot | undefined) => {
  if (slot && slot.recorder.state !== 'inactive') slot.recorder.stop();
};
const stopAll = (state: RecorderState) => {
  // Timeouts and intervals share one id list, so clearInterval clears both.
  state.timers.forEach(clearInterval);
  state.slots.forEach(stopSlot);
  state.timers = [];
  state.slots = [];
};
const waitForStop = (recorder: MediaRecorder) => new Promise<boolean>(resolve => {
  recorder.addEventListener('stop', () => resolve(true), {
    once: true
  });
  setTimeout(() => resolve(false), STOP_TIMEOUT_MS);
});
const takeOldestClip = async (state: RecorderState): Promise<Blob | undefined> => {
  // A recorder that failed has stopped on its own, while the other may still hold a good clip.
  const [oldest] = state.slots.filter((slot): slot is Slot => slot?.recorder.state === 'recording').sort((a, b) => a.startedAt - b.startedAt);
  const stopped = oldest ? waitForStop(oldest.recorder) : undefined;
  stopAll(state);
  if (!oldest || !stopped) return undefined;
  if (!(await stopped)) {
    logWarn('Selfie video recorder did not stop in time');
    return undefined;
  }
  // The server allows the bare mime type, so drop the codecs parameter. A browser may report none,
  // and the server rejects a clip without one.
  const type = (oldest.recorder.mimeType || getMimeType() || '').split(';')[0];
  return oldest.chunks.length > 0 ? new Blob(oldest.chunks, {
    type
  }) : undefined;
};

/** Records `stream` while `isRecording`, so `takeClip` can return the seconds before a capture. */
const useClipRecorder = (stream: MediaStream | null, isRecording: boolean) => {
  const state = useRef<RecorderState>({
    slots: [],
    timers: []
  });
  useEffect(() => {
    const mimeType = getMimeType();
    if (!stream || !isRecording || !mimeType) return undefined;
    const current = state.current;
    current.clip = undefined;
    const restart = (index: number) => {
      stopSlot(current.slots[index]);
      current.slots[index] = startSlot(stream, mimeType);
    };
    restart(0);
    current.timers = [setInterval(() => restart(0), PERIOD_MS), setTimeout(() => {
      restart(1);
      current.timers.push(setInterval(() => restart(1), PERIOD_MS));
    }, OFFSET_MS)];
    return () => stopAll(current);
  }, [stream, isRecording]);

  // Stable, since the capture handler that calls it feeds the auto-capture countdown's effect. A
  // second capture before recording restarts, like a double tap, gets the same clip: the first
  // already stopped every recorder.
  const takeClip = useCallback((): Promise<Blob | undefined> => {
    state.current.clip ??= takeOldestClip(state.current);
    return state.current.clip;
  }, []);
  return {
    takeClip
  };
};
export { canRecordVideo };
export default useClipRecorder;